/**
 * CTStatusCard — Displays current Controle Technique status for a vehicle
 *
 * Shows: result badge, expiry countdown, center, mileage, observations.
 * "Enregistrer un CT" button opens the recording modal.
 */
import React, { useState, useEffect } from 'react';
import { ClipboardCheck, Calendar, MapPin, Gauge, AlertTriangle, CheckCircle, XCircle, Clock, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { ctAPI } from '../../../api/index.js';
import { useFormat } from '../../../i18n/format.js';

const CT_RESULTS = {
  favorable: { label: 'Favorable', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  defavorable_mineur: { label: 'Défavorable (mineur)', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  defavorable_majeur: { label: 'Défavorable (majeur)', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  defavorable_critique: { label: 'Défavorable (critique)', color: 'bg-red-100 text-red-800', icon: XCircle },
  contrevisite: { label: 'Contre-visite requise', color: 'bg-red-100 text-red-800', icon: Clock },
};

function CTStatusCard({ vehicle, onRecordCT }) {
  const { fmt } = useFormat();
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (vehicle?.id) {
      ctAPI.getHistory(vehicle.id).then(res => {
        setHistory(res.data || []);
      }).catch(() => {});
    }
  }, [vehicle?.id]);

  const hasCT = vehicle?.ct_expiry_date;
  const resultConfig = CT_RESULTS[vehicle?.ct_result] || null;
  const ResultIcon = resultConfig?.icon || ClipboardCheck;

  // Compute days until expiry
  let daysUntil = null;
  let expiryStatus = 'none';
  if (vehicle?.ct_expiry_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(vehicle.ct_expiry_date);
    expiry.setHours(0, 0, 0, 0);
    daysUntil = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) expiryStatus = 'expired';
    else if (daysUntil <= 30) expiryStatus = 'critical';
    else if (daysUntil <= 90) expiryStatus = 'warning';
    else expiryStatus = 'valid';
  }

  return (
    <div className="space-y-4">
      {!hasCT ? (
        /* No CT on record */
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-gray-200">
              <ClipboardCheck className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="font-medium text-gray-700">Aucun contrôle technique enregistré</p>
              <p className="text-sm text-gray-500">Enregistrez le dernier CT pour activer le suivi</p>
            </div>
          </div>
          <button
            onClick={onRecordCT}
            className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      ) : (
        /* CT info display */
        <>
          {/* Status banner */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${
            expiryStatus === 'expired' ? 'bg-red-50 border-red-200' :
            expiryStatus === 'critical' ? 'bg-orange-50 border-orange-200' :
            expiryStatus === 'warning' ? 'bg-amber-50 border-amber-200' :
            'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                expiryStatus === 'expired' ? 'bg-red-200' :
                expiryStatus === 'critical' ? 'bg-orange-200' :
                expiryStatus === 'warning' ? 'bg-amber-200' :
                'bg-green-200'
              }`}>
                <ResultIcon className={`w-5 h-5 ${
                  expiryStatus === 'expired' ? 'text-red-700' :
                  expiryStatus === 'critical' ? 'text-orange-700' :
                  expiryStatus === 'warning' ? 'text-amber-700' :
                  'text-green-700'
                }`} />
              </div>
              <div>
                {resultConfig && (
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${resultConfig.color} mb-1`}>
                    {resultConfig.label}
                  </span>
                )}
                <p className={`font-semibold text-sm ${
                  expiryStatus === 'expired' ? 'text-red-800' :
                  expiryStatus === 'critical' ? 'text-orange-800' :
                  expiryStatus === 'warning' ? 'text-amber-800' :
                  'text-green-800'
                }`}>
                  {expiryStatus === 'expired'
                    ? `Expiré depuis ${Math.abs(daysUntil)} jour(s)`
                    : expiryStatus === 'critical'
                    ? `Expire dans ${daysUntil} jour(s)`
                    : expiryStatus === 'warning'
                    ? `Expire dans ${daysUntil} jour(s)`
                    : `Valide — expire dans ${daysUntil} jour(s)`
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onRecordCT}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Nouveau CT
            </button>
          </div>

          {/* Detail grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Dernier CT
              </p>
              <p className="font-medium text-gray-900">
                {vehicle.ct_issue_date ? fmt.date(vehicle.ct_issue_date) : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Expiration
              </p>
              <p className="font-medium text-gray-900">
                {fmt.date(vehicle.ct_expiry_date)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Centre
              </p>
              <p className="font-medium text-gray-900 text-sm">{vehicle.ct_center || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
                <Gauge className="w-3 h-3" /> Kilométrage
              </p>
              <p className="font-medium text-gray-900">
                {vehicle.ct_mileage ? `${fmt.number(vehicle.ct_mileage)} km` : '-'}
              </p>
            </div>
          </div>

          {/* Observations */}
          {vehicle.ct_observations && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase mb-1">Observations</p>
              <p className="text-sm text-gray-700">{vehicle.ct_observations}</p>
            </div>
          )}

          {/* History toggle */}
          {history.length > 1 && (
            <div>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Historique ({history.length} contrôles)
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2">
                  {history.map((insp, i) => (
                    <div key={insp.id || i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">
                          {insp.inspection_date ? fmt.date(insp.inspection_date) : '-'}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          insp.status === 'passed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {insp.status === 'passed' ? 'Favorable' : 'Défavorable'}
                        </span>
                      </div>
                      <span className="text-gray-500 text-xs">{insp.inspector_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CTStatusCard;
